/* eslint-disable no-param-reassign */
import uniq from "lodash/uniq";
import { Action, action } from "easy-peasy";
import { TransactionType } from "app/pages/explorer/components/transaction-type/data";

export const DEFAULT_YEAR_FILTER = new Date().getFullYear().toString();

export interface AppliedFiltersStateModel {
  // main
  startDate: string;
  endDate: string;
  countries: string[];
  sectors: string[];
  publishers: string[];
  activityStatus: string[];
  sdgs: string[];
  amountMin: number;
  amountMax: number;
  transactionType: TransactionType | null;
  search?: string;
  hasSdg: string;
  // activity
  activityScope: string[];
  documentLinkCategory: string[];
  hierarchy: string[];
  humanitarianScopeVocabulary: string[];
  humanitarianScopeType: string[];
  iatiVersion: string[];
  otherIdentifierType: string[];
  tag: string[];
  // default
  defaultAidType: string[];
  defaultAidTypeVocabulary: string[];
  currency: string[];
  defaultFinanceType: string[];
  defaultFlowType: string[];
  defaultLanguage: string[];
  defaultTiedStatus: string[];
  // organisation
  transactionReceiverOrganisation: string[];
  reportingOrganisationType: string[];
  collaborationType: string[];
  secondaryReporter: string[];
  // sector
  sectorVocabulary: string[];
  policyMarker: string[];
  // transaction
  transactionFlowType: string[];
  transactionTiedStatus: string[];
  transactionHumanitarian: string[];
  transactionValueCurrency: string[];
}

export interface AppliedFiltersModel extends AppliedFiltersStateModel {
  setStartDate: Action<AppliedFiltersModel, string>;
  setEndDate: Action<AppliedFiltersModel, string>;
  setCountries: Action<AppliedFiltersModel, string[]>;
  setSectors: Action<AppliedFiltersModel, string[]>;
  setPublishers: Action<AppliedFiltersModel, string[]>;
  setActivityStatus: Action<AppliedFiltersModel, string[]>;
  setSDGs: Action<AppliedFiltersModel, string[]>;
  setAmountMin: Action<AppliedFiltersModel, number>;
  setAmountMax: Action<AppliedFiltersModel, number>;
  setTransactionType: Action<AppliedFiltersModel, TransactionType>;
  setSearch: Action<AppliedFiltersModel, string>;
  setHasSdg: Action<AppliedFiltersModel, string>;
  setActivityScope: Action<AppliedFiltersModel, string[]>;
  setDocumentLinkCategory: Action<AppliedFiltersModel, string[]>;
  setHierarchy: Action<AppliedFiltersModel, string[]>;
  setHumanitarianScopeVocabulary: Action<AppliedFiltersModel, string[]>;
  setHumanitarianScopeType: Action<AppliedFiltersModel, string[]>;
  setIatiVersion: Action<AppliedFiltersModel, string[]>;
  setOtherIdentifierType: Action<AppliedFiltersModel, string[]>;
  setTag: Action<AppliedFiltersModel, string[]>;
  setDefaultAidType: Action<AppliedFiltersModel, string[]>;
  setDefaultAidTypeVocabulary: Action<AppliedFiltersModel, string[]>;
  setCurrency: Action<AppliedFiltersModel, string[]>;
  setDefaultFinanceType: Action<AppliedFiltersModel, string[]>;
  setDefaultFlowType: Action<AppliedFiltersModel, string[]>;
  setDefaultLanguage: Action<AppliedFiltersModel, string[]>;
  setDefaultTiedStatus: Action<AppliedFiltersModel, string[]>;
  setTransactionReceiverOrganisation: Action<AppliedFiltersModel, string[]>;
  setReportingOrganisationType: Action<AppliedFiltersModel, string[]>;
  setCollaborationType: Action<AppliedFiltersModel, string[]>;
  setSecondaryReporter: Action<AppliedFiltersModel, string[]>;
  setSectorVocabulary: Action<AppliedFiltersModel, string[]>;
  setPolicyMarker: Action<AppliedFiltersModel, string[]>;
  setTransactionFlowType: Action<AppliedFiltersModel, string[]>;
  setTransactionTiedStatus: Action<AppliedFiltersModel, string[]>;
  setTransactionHumanitarian: Action<AppliedFiltersModel, string[]>;
  setTransactionValueCurrency: Action<AppliedFiltersModel, string[]>;
  setAllActivityGroupFilters: Action<
    AppliedFiltersModel,
    {
      activityScope: string[];
      documentLinkCategory: string[];
      hierarchy: string[];
      humanitarianScopeVocabulary: string[];
      humanitarianScopeType: string[];
      iatiVersion: string[];
      otherIdentifierType: string[];
      tag: string[];
    }
  >;
  setAllDefaultGroupFilters: Action<
    AppliedFiltersModel,
    {
      defaultAidType: string[];
      defaultAidTypeVocabulary: string[];
      currency: string[];
      defaultFinanceType: string[];
      defaultFlowType: string[];
      defaultLanguage: string[];
      defaultTiedStatus: string[];
    }
  >;
  setAllOrganisationGroupFilters: Action<
    AppliedFiltersModel,
    {
      transactionReceiverOrganisation: string[];
      reportingOrganisationType: string[];
      collaborationType: string[];
      secondaryReporter: string[];
    }
  >;
  setAllSectorGroupFilters: Action<
    AppliedFiltersModel,
    {
      sectorVocabulary: string[];
      policyMarker: string[];
    }
  >;
  setAllTransactionGroupFilters: Action<
    AppliedFiltersModel,
    {
      transactionFlowType: string[];
      transactionTiedStatus: string[];
      transactionHumanitarian: string[];
      transactionValueCurrency: string[];
    }
  >;
  setAllFilters: Action<AppliedFiltersModel, AppliedFiltersStateModel>;
  resetAllFilters: Action<AppliedFiltersModel>;
}

export const defaultAppliedFilters = {
  startDate: "",
  endDate: "",
  // startDate: new Date(`${DEFAULT_YEAR_FILTER}-01-01`)
  //   .toISOString()
  //   .split("T")[0],
  // endDate: new Date(`${DEFAULT_YEAR_FILTER}-12-31`).toISOString().split("T")[0],
  countries: [] as string[],
  sectors: [] as string[],
  publishers: [] as string[],
  activityStatus: [] as string[],
  sdgs: [] as string[],
  amountMin: 0,
  amountMax: 0,
  transactionType: null,
  search: "",
  hasSdg: "",
  activityScope: [] as string[],
  documentLinkCategory: [] as string[],
  hierarchy: [] as string[],
  humanitarianScopeVocabulary: [] as string[],
  humanitarianScopeType: [] as string[],
  iatiVersion: [] as string[],
  otherIdentifierType: [] as string[],
  tag: [] as string[],
  defaultAidType: [] as string[],
  defaultAidTypeVocabulary: [] as string[],
  currency: [] as string[],
  defaultFinanceType: [] as string[],
  defaultFlowType: [] as string[],
  defaultLanguage: [] as string[],
  defaultTiedStatus: [] as string[],
  transactionReceiverOrganisation: [] as string[],
  reportingOrganisationType: [] as string[],
  collaborationType: [] as string[],
  secondaryReporter: [] as string[],
  sectorVocabulary: [] as string[],
  policyMarker: [] as string[],
  transactionFlowType: [] as string[],
  transactionTiedStatus: [] as string[],
  transactionHumanitarian: [] as string[],
  transactionValueCurrency: [] as string[],
};

export const appliedFilters: AppliedFiltersModel = {
  ...defaultAppliedFilters,
  setStartDate: action((state, payload: string) => {
    state.startDate = payload;
  }),
  setEndDate: action((state, payload: string) => {
    state.endDate = payload;
  }),
  setCountries: action((state, payload: string[]) => {
    state.countries = uniq(payload);
  }),
  setSectors: action((state, payload: string[]) => {
    state.sectors = uniq(payload);
  }),
  setPublishers: action((state, payload: string[]) => {
    state.publishers = uniq(payload);
  }),
  setActivityStatus: action((state, payload: string[]) => {
    state.activityStatus = uniq(payload);
  }),
  setSDGs: action((state, payload: string[]) => {
    state.sdgs = uniq(payload);
  }),
  setAmountMin: action((state, payload: number) => {
    state.amountMin = payload;
  }),
  setAmountMax: action((state, payload: number) => {
    state.amountMax = payload;
  }),
  setTransactionType: action((state, payload: TransactionType) => {
    state.transactionType = payload;
  }),
  setSearch: action((state, payload: string) => {
    state.search = payload;
  }),
  setHasSdg: action((state, payload: string) => {
    state.hasSdg = payload;
  }),
  setActivityScope: action((state, payload: string[]) => {
    state.activityScope = uniq(payload);
  }),
  setDocumentLinkCategory: action((state, payload: string[]) => {
    state.documentLinkCategory = uniq(payload);
  }),
  setHierarchy: action((state, payload: string[]) => {
    state.hierarchy = uniq(payload);
  }),
  setHumanitarianScopeVocabulary: action((state, payload: string[]) => {
    state.humanitarianScopeVocabulary = uniq(payload);
  }),
  setHumanitarianScopeType: action((state, payload: string[]) => {
    state.humanitarianScopeType = uniq(payload);
  }),
  setIatiVersion: action((state, payload: string[]) => {
    state.iatiVersion = uniq(payload);
  }),
  setOtherIdentifierType: action((state, payload: string[]) => {
    state.otherIdentifierType = uniq(payload);
  }),
  setTag: action((state, payload: string[]) => {
    state.tag = uniq(payload);
  }),
  setDefaultAidType: action((state, payload: string[]) => {
    state.defaultAidType = uniq(payload);
  }),
  setDefaultAidTypeVocabulary: action((state, payload: string[]) => {
    state.defaultAidTypeVocabulary = uniq(payload);
  }),
  setCurrency: action((state, payload: string[]) => {
    state.currency = uniq(payload);
  }),
  setDefaultFinanceType: action((state, payload: string[]) => {
    state.defaultFinanceType = uniq(payload);
  }),
  setDefaultFlowType: action((state, payload: string[]) => {
    state.defaultFlowType = uniq(payload);
  }),
  setDefaultLanguage: action((state, payload: string[]) => {
    state.defaultLanguage = uniq(payload);
  }),
  setDefaultTiedStatus: action((state, payload: string[]) => {
    state.defaultTiedStatus = uniq(payload);
  }),
  setTransactionReceiverOrganisation: action((state, payload: string[]) => {
    state.transactionReceiverOrganisation = uniq(payload);
  }),
  setReportingOrganisationType: action((state, payload: string[]) => {
    state.reportingOrganisationType = uniq(payload);
  }),
  setCollaborationType: action((state, payload: string[]) => {
    state.collaborationType = uniq(payload);
  }),
  setSecondaryReporter: action((state, payload: string[]) => {
    state.secondaryReporter = uniq(payload);
  }),
  setSectorVocabulary: action((state, payload: string[]) => {
    state.sectorVocabulary = uniq(payload);
  }),
  setPolicyMarker: action((state, payload: string[]) => {
    state.policyMarker = uniq(payload);
  }),
  setTransactionFlowType: action((state, payload: string[]) => {
    state.transactionFlowType = uniq(payload);
  }),
  setTransactionTiedStatus: action((state, payload: string[]) => {
    state.transactionTiedStatus = uniq(payload);
  }),
  setTransactionHumanitarian: action((state, payload: string[]) => {
    state.transactionHumanitarian = uniq(payload);
  }),
  setTransactionValueCurrency: action((state, payload: string[]) => {
    state.transactionValueCurrency = uniq(payload);
  }),
  setAllActivityGroupFilters: action(
    (
      state,
      payload: {
        activityScope: string[];
        documentLinkCategory: string[];
        hierarchy: string[];
        humanitarianScopeVocabulary: string[];
        humanitarianScopeType: string[];
        iatiVersion: string[];
        otherIdentifierType: string[];
        tag: string[];
      }
    ) => {
      state.activityScope = uniq(payload.activityScope);
      state.documentLinkCategory = uniq(payload.documentLinkCategory);
      state.hierarchy = uniq(payload.hierarchy);
      state.humanitarianScopeVocabulary = uniq(
        payload.humanitarianScopeVocabulary
      );
      state.humanitarianScopeType = uniq(payload.humanitarianScopeType);
      state.iatiVersion = uniq(payload.iatiVersion);
      state.otherIdentifierType = uniq(payload.otherIdentifierType);
      state.tag = uniq(payload.tag);
    }
  ),
  setAllDefaultGroupFilters: action(
    (
      state,
      payload: {
        defaultAidType: string[];
        defaultAidTypeVocabulary: string[];
        currency: string[];
        defaultFinanceType: string[];
        defaultFlowType: string[];
        defaultLanguage: string[];
        defaultTiedStatus: string[];
      }
    ) => {
      state.defaultAidType = uniq(payload.defaultAidType);
      state.defaultAidTypeVocabulary = uniq(payload.defaultAidTypeVocabulary);
      state.currency = uniq(payload.currency);
      state.defaultFinanceType = uniq(payload.defaultFinanceType);
      state.defaultFlowType = uniq(payload.defaultFlowType);
      state.defaultLanguage = uniq(payload.defaultLanguage);
      state.defaultTiedStatus = uniq(payload.defaultTiedStatus);
    }
  ),
  setAllOrganisationGroupFilters: action(
    (
      state,
      payload: {
        transactionReceiverOrganisation: string[];
        reportingOrganisationType: string[];
        collaborationType: string[];
        secondaryReporter: string[];
      }
    ) => {
      state.transactionReceiverOrganisation = uniq(
        payload.transactionReceiverOrganisation
      );
      state.reportingOrganisationType = uniq(payload.reportingOrganisationType);
      state.collaborationType = uniq(payload.collaborationType);
      state.secondaryReporter = uniq(payload.secondaryReporter);
    }
  ),
  setAllSectorGroupFilters: action(
    (
      state,
      payload: {
        sectorVocabulary: string[];
        policyMarker: string[];
      }
    ) => {
      state.sectorVocabulary = uniq(payload.sectorVocabulary);
      state.policyMarker = uniq(payload.policyMarker);
    }
  ),
  setAllTransactionGroupFilters: action(
    (
      state,
      payload: {
        transactionFlowType: string[];
        transactionTiedStatus: string[];
        transactionHumanitarian: string[];
        transactionValueCurrency: string[];
      }
    ) => {
      state.transactionFlowType = uniq(payload.transactionFlowType);
      state.transactionTiedStatus = uniq(payload.transactionTiedStatus);
      state.transactionHumanitarian = uniq(payload.transactionHumanitarian);
      state.transactionValueCurrency = uniq(payload.transactionValueCurrency);
    }
  ),
  resetAllFilters: action((state) => {
    state.startDate = "";
    state.endDate = "";
    state.countries = [];
    state.sectors = [];
    state.publishers = [];
    state.activityStatus = [];
    state.sdgs = [];
    state.amountMin = 0;
    state.amountMax = 0;
    state.search = "";
    state.hasSdg = "";
    state.activityScope = [];
    state.documentLinkCategory = [];
    state.hierarchy = [];
    state.humanitarianScopeVocabulary = [];
    state.humanitarianScopeType = [];
    state.iatiVersion = [];
    state.otherIdentifierType = [];
    state.tag = [];
    state.defaultAidType = [];
    state.defaultAidTypeVocabulary = [];
    state.currency = [];
    state.defaultFinanceType = [];
    state.defaultFlowType = [];
    state.defaultLanguage = [];
    state.defaultTiedStatus = [];
    state.transactionReceiverOrganisation = [];
    state.reportingOrganisationType = [];
    state.collaborationType = [];
    state.secondaryReporter = [];
    state.sectorVocabulary = [];
    state.policyMarker = [];
    state.transactionFlowType = [];
    state.transactionTiedStatus = [];
    state.transactionHumanitarian = [];
    state.transactionValueCurrency = [];
  }),
  setAllFilters: action((state, payload: AppliedFiltersStateModel) => {
    state.startDate = payload.startDate;
    state.endDate = payload.endDate;
    state.countries = uniq(payload.countries);
    state.sectors = uniq(payload.sectors);
    state.publishers = uniq(payload.publishers);
    state.activityStatus = uniq(payload.activityStatus);
    state.transactionType = payload.transactionType;
    state.search = payload.search;
    state.sdgs = uniq(payload.sdgs);
    state.hasSdg = payload.hasSdg;
    state.amountMin = payload.amountMin;
    state.amountMax = payload.amountMax;
    state.activityScope = uniq(payload.activityScope);
    state.documentLinkCategory = uniq(payload.documentLinkCategory);
    state.hierarchy = uniq(payload.hierarchy);
    state.humanitarianScopeVocabulary = uniq(
      payload.humanitarianScopeVocabulary
    );
    state.humanitarianScopeType = uniq(payload.humanitarianScopeType);
    state.iatiVersion = uniq(payload.iatiVersion);
    state.otherIdentifierType = uniq(payload.otherIdentifierType);
    state.tag = uniq(payload.tag);
    state.defaultAidType = uniq(payload.defaultAidType);
    state.defaultAidTypeVocabulary = uniq(payload.defaultAidTypeVocabulary);
    state.currency = uniq(payload.currency);
    state.defaultFinanceType = uniq(payload.defaultFinanceType);
    state.defaultFlowType = uniq(payload.defaultFlowType);
    state.defaultLanguage = uniq(payload.defaultLanguage);
    state.defaultTiedStatus = uniq(payload.defaultTiedStatus);
    state.transactionReceiverOrganisation = uniq(
      payload.transactionReceiverOrganisation
    );
    state.reportingOrganisationType = uniq(payload.reportingOrganisationType);
    state.collaborationType = uniq(payload.collaborationType);
    state.secondaryReporter = uniq(payload.secondaryReporter);
    state.sectorVocabulary = uniq(payload.sectorVocabulary);
    state.policyMarker = uniq(payload.policyMarker);
    state.transactionFlowType = uniq(payload.transactionFlowType);
    state.transactionTiedStatus = uniq(payload.transactionTiedStatus);
    state.transactionHumanitarian = uniq(payload.transactionHumanitarian);
    state.transactionValueCurrency = uniq(payload.transactionValueCurrency);
  }),
};
